import React from "react";
import useEntity from "../../../hooks/useRolePermission";
import {
    Box, 
    Paper, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Link,
    TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import { CheckBoxOutlined as CheckBoxOutlinedIcon, CheckBoxOutlineBlankOutlined as CheckBoxOutlineBlankOutlinedIcon } from "@mui/icons-material";
import {ID} from "../../../../types/global.d";
import EntityListHeader from "./RolePermissionListHeader";
import EntityListSearch from "./RolePermissionListSearch";
import {useLocation} from "react-router";
import RolePermissionListPagination from "./RolePermissionListPagination";
import * as Api from "../../../apis/RolePermission.api";
import { RolePermission } from "../../../types/models/RolePermission";
import { PrimaryRolePermission } from "../../../types/models/RolePermission";
import { usePagination } from '../../../../share';
import {displayDateTime, displayDate, displayTime} from "../../../../utils/formatDate.ts";
import {percentageFormat, fixedFormat, currencyFormat} from "../../../../utils/numberFormat";

import RoleLink from "../../../../auth/components/role/RoleDetail/RoleLink";
import RoleName from "../../../../auth/components/role/RoleDetail/RoleName";
import { toStringDisplay as toStringGroupType } from "../../../types/models/GroupType.d";

import { PrimaryRole } from "../../../../auth/types/models/Role";
import {useTheme} from "@mui/material/styles";
import useSearchDebounce from "../../../../hooks/useSearchDebounce";
import RolePermissionListItemTools from "./RolePermissionListItemTools";
import {useStateSession} from "../../../../hooks/useAuth";
import GradientCircularProgress from "../../../../share/components/progress/GradientCircularProgress";

import useMediaQuery from '@mui/material/useMediaQuery';
import { Trans } from "@lingui/react/macro";
import {
    mdiLinkVariant,
} from "@mdi/js";
import {
    StyledFillNavLink,
    ToolbarTableCell,
    BigTableRow,
    BigTableHeaderRow,
    SmallTableRow,
    SmallTableHeaderRow,
    StyledTableCell,
    StyledMdiIcon,
} from "../../../../share/components/styled/Table";
import {OrderBy, OrderDirection} from "../../../../types/global.d";

type IProps = {
    showAdd: boolean,
    roleId?: ID,
    permission?: string,
    };

const RolePermissionListItem = (props: IProps) => {
    
    const session = useStateSession()
    const {showAdd, ...query} = props;

    const { page, pageSize, handleChangePage, handleChangePageSize } = usePagination('RolePermission')

    
    
    
    
    const [, setSearchInputValue, searchInputTerm] = useSearchDebounce();
    const [searchInputAdvanced, setSearchInputAdvanced] = React.useState(undefined);

    const [orderBys, setOrderBys] = React.useState<OrderBy[]>([]);

    const toggleOrder = React.useCallback((name: string) => {
        setOrderBys(prev => {
            const current = prev.find(item => item.name === name)
            return [
                {
                    name,
                    direction: current?.direction === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc
                },
                ...(prev.filter(item => item.name !== name))
            ]
        });
    }, []);

    const { data: dataSimple = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
    }, 
        isLoading: isLoadingSimple,
        isFetching: isFetchingSimple,
        refetch: refetchSimple,
    } =
          Api.useGetRolePermissionPresentationQuery({
                  list: { page, pageSize },
                  filter: {
                    roleIdRefs: (PrimaryRole.fromQuery(query.roleId)) ? [ PrimaryRole.fromQuery(query.roleId) ] : [],
                    // for now not supported to query here other types
                    roleId: null,
                    permission: searchInputTerm || '',
                    permissionOr: true,
                    orderBys,
                  },
              }, {skip: !!searchInputAdvanced})
    
    const { data: dataAdvanced = {
        items: [],
        totalItems: 0,
        page,
        pageSize,
        numPages: 0
        }, 
        isLoading: isLoadingAdvanced,
        isFetching: isFetchingAdvanced,
        refetch: refetchAdvanced,
    } =
        Api.useGetRolePermissionAdvancedPresentationQuery({
                list: { page, pageSize },
                filter: {

                    roleIdRefs: (PrimaryRole.fromQuery(query.roleId)) ? [ PrimaryRole.fromQuery(query.roleId) ] : [],
                    ...searchInputAdvanced,
                    orderBys,
                },
            }, {skip: !searchInputAdvanced})
    const {data, isLoading, isFetching, refetch} =
        searchInputAdvanced ? 
            {
                data: dataAdvanced,
                isLoading: isLoadingAdvanced,
                isFetching: isFetchingAdvanced,
                refetch: refetchAdvanced,
            } 
            :
        {
                data: dataSimple,
                isLoading: isLoadingSimple,
                isFetching: isFetchingSimple,
                refetch: refetchSimple,
            }

    const routeLocation = useLocation();
    
    const onClickUrl = (entity: RolePermission) => routeLocation.pathname.endsWith('role-permissions') ?
        `${PrimaryRolePermission.fromEntity(entity).toPath()}`
        :
        `role-permissions/${PrimaryRolePermission.fromEntity(entity).toPath()}`

    const onEditUrl = (entity: RolePermission) => `${onClickUrl(entity)}/edit`

    
    
    return React.useMemo(() => (
        <Box>
            <Paper>
                <EntityListHeader showAdd={showAdd} {...query}/>
                <EntityListSearch
                    setSearchInputValue={setSearchInputValue}
                    searchInputAdvanced={searchInputAdvanced} setSearchInputAdvanced={setSearchInputAdvanced}
                    query={query}
                    refetch={refetch}
                />
                <TableContainer>

                    

                    

                    

                    

                    
        
                    
                        <Table stickyHeader data-testid="RolePermission-entity-list" size="small">
                            <TableHead>
                                <BigTableHeaderRow>
                                    
                                    
                                    
                                    { query.roleId ?
                                        <></>
                                        :
                                        <StyledTableCell align="left">
                                            
                                            <TableSortLabel
                                                active={ orderBys.find((item) => item.name === 'roleId') }
                                                direction={ orderBys.find((item) => item.name === 'roleId')?.direction || 'asc' }
                                                onClick={() => toggleOrder('roleId')}
                                            >
                                            
                                                <Trans>Role</Trans>
                                            
                                            </TableSortLabel>
                                            
                                        </StyledTableCell>
                                    }
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="left">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'permission') }
                                            direction={ orderBys.find((item) => item.name === 'permission')?.direction || 'asc' }
                                            onClick={() => toggleOrder('permission')}
                                        >
                                        
                                            <Trans>Permission</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                    <StyledTableCell align="center">
                                        
                                        <TableSortLabel
                                            active={ orderBys.find((item) => item.name === 'createdAt') }
                                            direction={ orderBys.find((item) => item.name === 'createdAt')?.direction || 'asc' }
                                            onClick={() => toggleOrder('createdAt')}
                                        >
                                        
                                            <Trans>Created At</Trans>
                                        
                                        </TableSortLabel>
                                        
                                    </StyledTableCell>
                                    
                                    
                                    
                                    
                                </BigTableHeaderRow>
                            </TableHead>
                            <TableBody>
                                { data.items.map((item) => (
                                    <BigTableRow
                                        hover={true}
                                        key={ PrimaryRolePermission.fromEntity(item).toPath() }
                                    >
                                        
                                        
                                        
                        { query.roleId ? <></> :
                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            
                        
                        
                        <RoleLink id={ item.roleId } icon={<StyledMdiIcon d={mdiLinkVariant} />}/>
                        
                        
                    
                            
                        </TableCell>
                        
                        }
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="left"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        { item.permission || '-' }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        
                        <TableCell
                            scope="row"
                            align="center"
                            
                        >
                            
                            <Link
                                component={StyledFillNavLink}
                                to={ onClickUrl(item) }
                                viewTransition={true}
                            >
                            
                            
                        
                        {displayDateTime(item.createdAt) }
                        
                    
                            
                            </Link>
                            
                        </TableCell>
                        
                    
                                        
                                        
                                        
                                        <ToolbarTableCell align="center" width="15%">
                                            <RolePermissionListItemTools data={ item }
                                                                               onClickUrl={onClickUrl(item)} onEditUrl={onEditUrl(item)}
                                                                                />
                                        </ToolbarTableCell>
                                    </BigTableRow>
                                ))}
                            </TableBody>
                        </Table>
            
                    { !data.totalItems && !isLoading && !isFetching &&
                        <Typography
                            sx={ { flex: '1 1 100%', padding: "2em", } }
                            component="div"
                        >
                            <Trans>No Role Permissions</Trans>
                        </Typography>
                    }
                    { (isLoading || isFetching) &&
                        <GradientCircularProgress/>
                    }
                </TableContainer>
                <RolePermissionListPagination
                    totalItems={ data.totalItems }
                    page={Number(page)}
                    pageSize={pageSize}
                    handleChangePage={handleChangePage}
                    handleChangePageSize={handleChangePageSize}
                />
            </Paper>
        </Box>
    ), [isLoading, isFetching, data, page, pageSize, onClickUrl,  onEditUrl])
}

export default RolePermissionListItem;
